import React, { memo, useMemo, useCallback } from 'react';
import FileIcon from '@mui/icons-material/Description';
import { type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import { DraftTableContainer, StyledDataGrid, ActionButton } from './DynamicForm.styles.index';


export interface DraftPreview {
  id: string;
  lastUpdated: string;
  previewText: string;
}

interface DraftTableProps {
  availableDrafts: DraftPreview[];
  onLoadDraft: (draftId: string) => void;
}

export const DraftTable: React.FC<DraftTableProps> = memo(({
  availableDrafts,
  onLoadDraft,
}) => {
  const handleLoadDraft = useCallback((draftId: string) => {
    try {
      if (onLoadDraft && typeof onLoadDraft === 'function') {
        onLoadDraft(draftId);
      } else {
        console.warn('onLoadDraft callback not available');
      }
    } catch (error) {
      console.error('Error in handleLoadDraft:', error);
      // Don't throw - let the parent component handle errors
    }
  }, [onLoadDraft]);

  // Memoize draft columns to avoid recreation on each render
  const draftColumns: GridColDef[] = useMemo(() => [
    {
      field: 'preview',
      headerName: 'Details',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<{ preview: string; lastUpdated: string; id: string }>) => (
        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', width: '100%' }}>
          {params.value as string}
        </div>
      ),
    },
    {
      field: 'lastUpdated',
      headerName: 'Last Updated',
      minWidth: 160,
      renderCell: (params: GridRenderCellParams<{ preview: string; lastUpdated: string; id: string }>) => {
        const date = new Date(params.value as string);
        return date.toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        });
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams<{ preview: string; lastUpdated: string; id: string }>) => (
        <ActionButton
          onClick={() => handleLoadDraft(params.row.id)}
          variant='primary'
          icon={FileIcon}
          aria-label={`Edit draft ${params.row.id}`}
          type="button"
        >
          Edit
        </ActionButton>
      ),
    },
  ], [handleLoadDraft]);

  if (!availableDrafts || availableDrafts.length === 0) {
    return null;
  }

  return (
    <DraftTableContainer>
      <StyledDataGrid
        density="comfortable"
        rows={availableDrafts.map((draft) => ({
          id: draft.id,
          preview: draft.previewText || 'Draft',
          lastUpdated: draft.lastUpdated,
        }))}
        getRowHeight={() => 'auto'}
        columns={draftColumns}
        disableRowSelectionOnClick
        hideFooter
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
      />
    </DraftTableContainer>
  );
});

DraftTable.displayName = 'DraftTable';

